import { useState, useEffect, useRef } from "react";

const STORAGE_KEY = "metriport_custom_keywords";

type UseCustomKeywords = {
  customKeywords: string[];
  addKeyword: (keyword: string) => boolean;
  removeKeyword: (keyword: string) => void;
  clearAll: () => void;
};

export function useCustomKeywords(): UseCustomKeywords {
  const [customKeywords, setCustomKeywords] = useState<string[]>(() => loadFromStorage());
  const isMountedRef = useRef(false);

  // Save to localStorage whenever the list changes
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    saveToStorage(customKeywords);
  }, [customKeywords]);

  // Listen for changes from other components/tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const newKeywords = JSON.parse(e.newValue) as string[];
          setCustomKeywords(newKeywords);
        } catch (error) {
          console.error("Failed to parse storage change:", error);
        }
      }
    };

    // Also listen for custom events (for same-window updates)
    const handleCustomEvent = () => {
      setCustomKeywords(loadFromStorage());
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("customKeywordsChanged", handleCustomEvent);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("customKeywordsChanged", handleCustomEvent);
    };
  }, []);

  function addKeyword(keyword: string): boolean {
    const trimmedKeyword = keyword.trim();
    if (!trimmedKeyword) return false;
    const exists = customKeywords.some(kw => kw.toLowerCase() === trimmedKeyword.toLowerCase());
    if (exists) return false;
    setCustomKeywords(prev => [...prev, trimmedKeyword]);
    return true;
  }

  function removeKeyword(keyword: string) {
    setCustomKeywords(prev => prev.filter(kw => kw !== keyword));
  }

  function clearAll() {
    setCustomKeywords([]);
    clearStorage();
  }

  return {
    customKeywords,
    addKeyword,
    removeKeyword,
    clearAll,
  };
}

function loadFromStorage(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored) as string[];
  } catch (error) {
    console.error("Failed to load custom keywords from localStorage:", error);
    return [];
  }
}

function saveToStorage(keywords: string[]): void {
  try {
    const newValue = JSON.stringify(keywords);
    const currentValue = localStorage.getItem(STORAGE_KEY);

    // Only save and emit event if the value has actually changed
    if (newValue === currentValue) return;

    localStorage.setItem(STORAGE_KEY, newValue);
    // Dispatch custom event to notify other components in the same window
    window.dispatchEvent(new Event("customKeywordsChanged"));
  } catch (error) {
    console.error("Failed to save custom keywords to localStorage:", error);
  }
}

function clearStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    // Dispatch custom event to notify other components in the same window
    window.dispatchEvent(new Event("customKeywordsChanged"));
  } catch (error) {
    console.error("Failed to clear custom keywords from localStorage:", error);
  }
}
